// SKFitter (Quantum Materials) in a project: the desktop program's four screens as steps -
// 1 DFT data, 2 fit set-up, 3 fit result, 4 Wannier export and properties.
import { Accordion, Alert, Anchor, Card, Group, Loader, Stack, Stepper, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { Markdown } from "../../components/Markdown";
import { patch, type ProjectDetail } from "../../lib/api";
import { stepHint, useExample } from "../../lib/examples";
import { Specialities } from "../Examples";
import { useLiveJob, useMe } from "../../lib/hooks";
import { seedFrom, useSkForms, type DftResult, type SkMemory } from "../../lib/skfitter";
import { DftStep } from "./DftStep";
import { PropertiesStep } from "./PropertiesStep";
import { ResultStep } from "./ResultStep";
import { SetupStep } from "./SetupStep";
import { examplesPath } from "../../lib/modules";

export function SkfitterWorkspace({ project }: { project: ProjectDetail }) {
  const forms = useSkForms();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const saved = project.settings?.skfitter as SkMemory | undefined;
  const [mem, setMem] = useState<SkMemory>(() => saved ?? { code: "vasp", files: {}, seed: seedFrom(project.name) });
  const [step, setStep] = useState<number>(saved?.fit_job ? 2 : saved?.dft_job ? 1 : 0);

  // remember the state in the project (the desktop keeps it in the project folder)
  const latest = useRef(mem);
  latest.current = mem;
  const remember = useCallback((next: Partial<SkMemory>) => {
    const merged = { ...latest.current, ...next };
    setMem(merged);
    patch(`/projects/${project.id}`, { settings: { ...(project.settings ?? {}), skfitter: merged } })
      .then(() => qc.invalidateQueries({ queryKey: ["project", project.id] }))
      .catch(() => undefined);
  }, [project.id, project.settings, qc]);
  const update = useCallback((next: Partial<SkMemory>) => setMem((m) => ({ ...m, ...next })), []);

  const { job: dftJob } = useLiveJob(mem.dft_job);
  const dft = dftJob?.status === "succeeded" ? (dftJob.result as DftResult) : undefined;
  const { job: fitJob } = useLiveJob(mem.fit_job);
  const hasFit = fitJob?.status === "succeeded";
  useEffect(() => { if (step === 1 && !dft && dftJob && dftJob.status !== "running" && dftJob.status !== "queued") setStep(0); },
    [step, dft, dftJob]);

  const exampleId = (project.settings?.example as string | undefined) ?? mem.example;
  const { data: example } = useExample(exampleId);

  if (forms.isLoading || !forms.data) return <Card withBorder><Loader size="sm" /></Card>;
  if (me && !(project.limits ?? me.limits)["skfitter.enabled"])
    return <Alert color="orange" title="Not included in your plan">Tight Binding is not part of your plan.</Alert>;

  const hint = example ? stepHint(example.steps, step) : "";
  return (
    <Stack gap="md">
    {example && (
      <Card withBorder data-testid="example-banner">
        <Stack gap={6}>
          <Group justify="space-between" wrap="wrap">
            <Text fw={600}>Illustrative project: {example.name} <Text span c="dimmed" fw={400}>· {example.material}</Text></Text>
            <Anchor component={Link} to={examplesPath("tight-binding")} size="sm">All illustrative projects</Anchor>
          </Group>
          <Specialities items={example.speciality} />
          {project.advanced_resources && <Text size="sm" c="teal" data-testid="example-advanced">This project runs with the
            Advanced plan's resources (property calculations, larger fits, longer jobs), granted by an administrator.</Text>}
          {hint && <Alert color="blue" p="xs" data-testid="example-hint"><Text size="sm"><b>Step {step + 1}:</b> {hint}</Text></Alert>}
          <Accordion variant="contained" defaultValue={step === 0 ? "guide" : null}>
            <Accordion.Item value="guide">
              <Accordion.Control><Text size="sm" fw={600}>Guide: what makes the fit and the calculations work</Text></Accordion.Control>
              <Accordion.Panel><Markdown source={example.guide ?? ""} /></Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Stack>
      </Card>
    )}
    <Card withBorder>
      <Stepper active={step} onStepClick={setStep} size="sm" allowNextStepsSelect={false}>
        <Stepper.Step label="DFT data" description={dft ? `${dft.meta.formula} · ${dft.meta.spacegroup}` : "VASP or Quantum ESPRESSO"}>
          <DftStep project={project} forms={forms.data} mem={mem} update={update} remember={remember} dftJob={dftJob}
            onNext={() => setStep(1)} />
        </Stepper.Step>
        <Stepper.Step label="Fit set-up" description="orbitals, bands, optimiser" disabled={!dft}>
          {dft && (
            <SetupStep project={project} forms={forms.data} dft={dft} dftJobId={mem.dft_job!} mem={mem} update={update}
              remember={remember} onBack={() => setStep(0)} onFitted={(id) => { remember({ fit_job: id }); setStep(2); }} />
          )}
        </Stepper.Step>
        <Stepper.Step label="Fit result" description="bands, errors, parameters" disabled={!hasFit}>
          {fitJob && <ResultStep project={project} job={fitJob} dftJobId={dft ? mem.dft_job : undefined} onOpen={(id) => remember({ fit_job: id })} onBack={() => setStep(1)}
            onNext={() => setStep(3)} />}
        </Stepper.Step>
        <Stepper.Step label="Export & properties" description="Wannier export, band check, Properties" disabled={!hasFit}>
          {dft && fitJob && hasFit && <PropertiesStep project={project} forms={forms.data} dft={dft} fitJob={fitJob} mem={mem}
            update={update} remember={remember} onBack={() => setStep(2)} />}
        </Stepper.Step>
      </Stepper>
    </Card>
    </Stack>
  );
}
